import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeAudioChunk } from '../../../services/transcription.service.ts';
import type { TranscriptLine } from '../types/minutes.types.ts';
import { audioBlobToWav } from '../utils/audioBlobToWav.ts';

/** Tam WebM dosyası için stop/start aralığı */
const SEGMENT_MS = 12_000;
/** ~1 sn 16 kHz mono WAV altı (sessizlik / yarım segment) gönderilmez */
const MIN_WAV_BYTES = 32_000;
const MIN_WEBM_BYTES = 2_000;

function getAudioMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? 'audio/webm';
}

export interface UseMeetingMinutesOptions {
  meetingId: string | undefined;
  isHost: boolean;
  localStream: MediaStream | null;
  remoteAudioStreams: MediaStream[];
  hostDisplayName: string;
  broadcastMinutesPolicy: (enabled: boolean) => void;
  onRemoteMinutesPolicy?: (enabled: boolean) => void;
}

export function useMeetingMinutes({
  meetingId,
  isHost,
  localStream,
  remoteAudioStreams,
  hostDisplayName,
  broadcastMinutesPolicy,
}: UseMeetingMinutesOptions) {
  const [minutesEnabled, setMinutesEnabled] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [sttAvailable, setSttAvailable] = useState<boolean | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const minutesEnabledRef = useRef(false);
  const captureActiveRef = useRef(false);
  const transcriptRef = useRef<TranscriptLine[]>([]);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const mixCtxRef = useRef<AudioContext | null>(null);
  const destRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const sourcesRef = useRef<MediaStreamAudioSourceNode[]>([]);
  const startedAtRef = useRef<number>(Date.now());
  const segmentTimerRef = useRef<number | null>(null);
  const flushingRef = useRef(false);
  const mimeTypeRef = useRef('audio/webm');
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const localStreamRef = useRef(localStream);
  const remoteStreamsRef = useRef(remoteAudioStreams);
  localStreamRef.current = localStream;
  remoteStreamsRef.current = remoteAudioStreams;

  minutesEnabledRef.current = minutesEnabled;
  transcriptRef.current = transcript;

  const clearSegmentTimer = useCallback(() => {
    if (segmentTimerRef.current != null) {
      window.clearInterval(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }
  }, []);

  const stopCapture = useCallback(() => {
    captureActiveRef.current = false;
    clearSegmentTimer();
    flushingRef.current = false;
    if (recorderRef.current) {
      const rec = recorderRef.current;
      recorderRef.current = null;
      rec.ondataavailable = null;
      rec.onstop = null;
      try {
        if (rec.state !== 'inactive') rec.stop();
      } catch {
        /* ignore */
      }
    }
    sourcesRef.current.forEach((s) => {
      try {
        s.disconnect();
      } catch {
        /* ignore */
      }
    });
    sourcesRef.current = [];
    if (mixCtxRef.current) {
      void mixCtxRef.current.close().catch(() => undefined);
      mixCtxRef.current = null;
    }
    destRef.current = null;
    setIsTranscribing(false);
  }, [clearSegmentTimer]);

  const appendTranscript = useCallback((speaker: string, text: string) => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length < 2) return;
    const junk =
      /^(altyazı|subtitle|thanks for watching|thank you\.?|amk\.?|mk\.?|\(.+\))$/i;
    if (junk.test(trimmed) || /altyazı/i.test(trimmed)) return;

    const prev = transcriptRef.current;
    const last = prev[prev.length - 1];
    // Aynı konuşmacının ardışık parçalarını birleştir (daha okunaklı tutanak)
    if (
      last &&
      last.speaker === speaker &&
      Date.now() - last.at < 20_000 &&
      last.text.length < 400
    ) {
      const merged: TranscriptLine = {
        ...last,
        text: `${last.text} ${trimmed}`.replace(/\s+/g, ' ').trim(),
        at: Date.now(),
      };
      transcriptRef.current = [...prev.slice(0, -1), merged];
      setTranscript(transcriptRef.current);
      return;
    }

    const line: TranscriptLine = {
      id: `line_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      speaker,
      text: trimmed,
      at: Date.now(),
      offsetMs: Date.now() - startedAtRef.current,
    };
    transcriptRef.current = [...prev, line];
    setTranscript(transcriptRef.current);
  }, []);

  const uploadChunk = useCallback(
    (blob: Blob) => {
      if (!meetingId || blob.size < MIN_WEBM_BYTES) return;
      uploadQueueRef.current = uploadQueueRef.current
        .then(async () => {
          try {
            let payload = blob;
            let filename = `chunk-${Date.now()}.webm`;
            try {
              payload = await audioBlobToWav(blob);
              filename = `chunk-${Date.now()}.wav`;
            } catch (err) {
              console.warn('[TBKMeet] WAV dönüşümü başarısız, ham blob gönderiliyor:', err);
            }

            if (payload.type.includes('wav') && payload.size < MIN_WAV_BYTES) {
              console.log('[TBKMeet] STT skip (çok kısa)', payload.size, 'bytes');
              return;
            }

            console.log('[TBKMeet] STT upload', filename, payload.size, 'bytes');
            const result = await transcribeAudioChunk({
              meetingId,
              blob: payload,
              filename,
              speakerHint: hostDisplayName || 'Katılımcılar',
            });
            if (result?.text) {
              setSttAvailable(true);
              setLastError(null);
              appendTranscript(result.speaker || hostDisplayName || 'Katılımcılar', result.text);
            } else {
              setSttAvailable((prev) => (prev === true ? true : false));
            }
          } catch (err) {
            console.warn('[TBKMeet] STT upload hata:', err);
            setLastError(err instanceof Error ? err.message : 'STT hatası');
            setSttAvailable(false);
          }
        })
        .catch(() => undefined);
    },
    [meetingId, appendTranscript, hostDisplayName]
  );

  const flushSegment = useCallback(
    (opts?: { restart?: boolean }) => {
      const shouldRestart = opts?.restart !== false;
      const recorder = recorderRef.current;
      if (!recorder || recorder.state !== 'recording' || flushingRef.current) return;
      flushingRef.current = true;
      const parts: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) parts.push(event.data);
      };

      recorder.onstop = () => {
        flushingRef.current = false;
        if (parts.length > 0) {
          uploadChunk(new Blob(parts, { type: mimeTypeRef.current }));
        }
        if (
          !shouldRestart ||
          !captureActiveRef.current ||
          !minutesEnabledRef.current ||
          !recorderRef.current ||
          !destRef.current
        ) {
          return;
        }
        try {
          if (recorderRef.current.state === 'inactive') {
            recorderRef.current.start();
          }
        } catch (err) {
          console.warn('[TBKMeet] MediaRecorder yeniden başlatılamadı:', err);
          try {
            const next = new MediaRecorder(destRef.current.stream, {
              mimeType: mimeTypeRef.current,
            });
            recorderRef.current = next;
            next.start();
          } catch (err2) {
            console.warn('[TBKMeet] MediaRecorder oluşturulamıyor:', err2);
          }
        }
      };

      try {
        recorder.requestData();
        recorder.stop();
      } catch {
        flushingRef.current = false;
      }
    },
    [uploadChunk]
  );

  const startCapture = useCallback(async () => {
    if (!meetingId || typeof MediaRecorder === 'undefined') return;

    stopCapture();
    // İlk açılışta zamanı sabitle; stream yenilenince sıfırlama (süre/offset bozulmasın)
    if (!startedAtRef.current || transcriptRef.current.length === 0) {
      startedAtRef.current = Date.now();
    }

    try {
      const ctx = new AudioContext();
      await ctx.resume();
      const dest = ctx.createMediaStreamDestination();
      const gain = ctx.createGain();
      // 1.0 — yükseltme bozulma yapıp Whisper doğruluğunu düşürür
      gain.gain.value = 1.0;
      gain.connect(dest);

      mixCtxRef.current = ctx;
      destRef.current = dest;

      const connectStream = (stream: MediaStream | null | undefined) => {
        if (!stream) return;
        const audioTracks = stream.getAudioTracks().filter((t) => t.readyState === 'live');
        if (audioTracks.length === 0) return;
        const audioOnly = new MediaStream(audioTracks);
        const source = ctx.createMediaStreamSource(audioOnly);
        source.connect(gain);
        sourcesRef.current.push(source);
      };

      connectStream(localStreamRef.current);
      remoteStreamsRef.current.forEach(connectStream);

      if (sourcesRef.current.length === 0) {
        console.warn('[TBKMeet] Tutanak: bağlanacak canlı ses track’i yok');
        setLastError('Mikrofon/ses track bulunamadı — mikrofon izni verin');
        void ctx.close();
        mixCtxRef.current = null;
        return;
      }

      const localTracks = localStreamRef.current?.getAudioTracks() ?? [];
      const micLive = localTracks.some((t) => t.readyState === 'live' && t.enabled);
      if (!micLive) {
        setLastError('Mikrofon kapalı — tutanak için mikrofonu açın');
      }

      const mimeType = getAudioMimeType();
      mimeTypeRef.current = mimeType;
      const recorder = new MediaRecorder(dest.stream, {
        mimeType,
        audioBitsPerSecond: 128_000,
      });
      recorderRef.current = recorder;
      captureActiveRef.current = true;
      recorder.start();
      setIsTranscribing(true);
      if (micLive) setLastError(null);

      clearSegmentTimer();
      segmentTimerRef.current = window.setInterval(() => {
        if (!captureActiveRef.current || !minutesEnabledRef.current) return;
        flushSegment({ restart: true });
      }, SEGMENT_MS);
    } catch (error) {
      console.warn('[TBKMeet] Tutanak yakalama başlatılamadı:', error);
      setLastError(error instanceof Error ? error.message : 'Yakalama hatası');
      stopCapture();
    }
  }, [meetingId, stopCapture, flushSegment, clearSegmentTimer]);

  const hasLocalStream = !!localStream;

  useEffect(() => {
    if (!isHost || !minutesEnabled || !hasLocalStream) return;
    void startCapture();
    return () => stopCapture();
    // localStream referansı (track replace) yüzünden sürekli restart etme
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isHost,
    minutesEnabled,
    meetingId,
    hasLocalStream,
    remoteAudioStreams.length,
    startCapture,
    stopCapture,
  ]);

  const applyRemoteMinutesPolicy = useCallback(
    (enabled: boolean) => {
      setMinutesEnabled(enabled);
      if (!enabled) stopCapture();
    },
    [stopCapture]
  );

  const enableMinutes = useCallback(() => {
    if (!isHost) return;
    transcriptRef.current = [];
    setTranscript([]);
    startedAtRef.current = Date.now();
    setMinutesEnabled(true);
    broadcastMinutesPolicy(true);
  }, [isHost, broadcastMinutesPolicy]);

  const disableMinutes = useCallback(() => {
    if (!isHost) return;
    captureActiveRef.current = false;
    clearSegmentTimer();
    if (recorderRef.current?.state === 'recording') {
      flushSegment({ restart: false });
    }
    setMinutesEnabled(false);
    broadcastMinutesPolicy(false);
    window.setTimeout(() => stopCapture(), 500);
  }, [isHost, broadcastMinutesPolicy, stopCapture, flushSegment, clearSegmentTimer]);

  const clearTranscript = useCallback(() => {
    transcriptRef.current = [];
    setTranscript([]);
  }, []);

  /** Son segmenti gönderip upload kuyruğunu bitirir (çıkışta tutanak kaydı için). */
  const flushPendingTranscription = useCallback(async () => {
    captureActiveRef.current = false;
    clearSegmentTimer();
    if (recorderRef.current?.state === 'recording') {
      flushSegment({ restart: false });
      await new Promise((r) => window.setTimeout(r, 800));
    }
    await uploadQueueRef.current.catch(() => undefined);
    await new Promise((r) => window.setTimeout(r, 50));
    return transcriptRef.current;
  }, [flushSegment, clearSegmentTimer]);

  return {
    minutesEnabled,
    isTranscribing,
    transcript,
    sttAvailable,
    lastError,
    enableMinutes,
    disableMinutes,
    applyRemoteMinutesPolicy,
    clearTranscript,
    setTranscript,
    flushPendingTranscription,
    minutesStartedAt: startedAtRef,
  };
}
